import { AnimatePresence, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useCatalog } from "@/data/catalog";
import { formatPrice } from "@/data/products";

export const CartDrawer = () => {
  const { drawerOpen, setDrawerOpen, items, updateQty, removeItem, subtotal } = useCart();
  const { getProduct } = useCatalog();
  const navigate = useNavigate();

  const startCheckout = () => {
    try {
      const lines = items.map((item) => {
        const p = getProduct(item.id);
        const ecwidId = item.ecwidId || p?.ecwidId;
        if (!ecwidId) throw new Error(`"${item.name}" isn't linked to the store yet — remove it and add it again.`);
        const options = {};
        if (p && !(p.sizes.length === 1 && p.sizes[0] === "One Size")) options.Size = item.size;
        if (p && p.colors.length > 1) options.Color = item.color;
        return { productId: ecwidId, quantity: item.qty, options, name: item.name };
      });
      // The Checkout page picks these up after the Ecwid widget initializes.
      window.__mvmPendingCheckout = lines;
      setDrawerOpen(false);
      navigate("/checkout#!/~/cart");
    } catch (e) {
      toast.error(e.message || "Couldn't start checkout. Please try again.");
    }
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            className="fixed inset-0 z-[85] bg-obsidian/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed top-0 right-0 bottom-0 z-[86] w-full max-w-md bg-abyss border-l border-stone/10 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between px-7 py-6 border-b border-stone/10">
              <h2 className="font-display text-xl uppercase tracking-wide">Your Bag</h2>
              <button data-testid="cart-close-button" onClick={() => setDrawerOpen(false)} aria-label="Close bag" className="text-stone/70 hover:text-stone">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6" data-lenis-prevent>
              {items.length === 0 && (
                <div data-testid="cart-empty" className="h-full flex flex-col items-center justify-center text-center gap-4">
                  <p className="text-stone/50">Your bag is empty.</p>
                  <Link
                    to="/shop"
                    data-testid="cart-empty-shop-link"
                    onClick={() => setDrawerOpen(false)}
                    className="font-display uppercase tracking-[0.2em] text-sm border border-stone/30 px-6 py-3 hover:bg-stone hover:text-obsidian transition-colors"
                  >
                    Shop the range
                  </Link>
                </div>
              )}
              {items.map((item) => (
                <div key={item.key} data-testid={`cart-item-${item.key}`} className="flex gap-4">
                  <img src={item.image} alt={item.name} className="w-20 h-24 object-cover bg-deep" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <p className="font-display uppercase tracking-wide">{item.name}</p>
                      <button
                        data-testid={`cart-remove-${item.key}`}
                        onClick={() => removeItem(item.key)}
                        aria-label={`Remove ${item.name}`}
                        className="text-stone/40 hover:text-stone"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <p className="text-xs text-stone/50 mt-1 uppercase tracking-wider">
                      {item.color} / {item.size}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-stone/20">
                        <button
                          data-testid={`cart-qty-minus-${item.key}`}
                          onClick={() => updateQty(item.key, -1)}
                          className="px-2.5 py-1.5 text-stone/70 hover:text-stone"
                          aria-label="Decrease quantity"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-2 text-sm">{item.qty}</span>
                        <button
                          data-testid={`cart-qty-plus-${item.key}`}
                          onClick={() => updateQty(item.key, 1)}
                          className="px-2.5 py-1.5 text-stone/70 hover:text-stone"
                          aria-label="Increase quantity"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <p className="text-sm text-mist">{formatPrice(item.price * item.qty)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t border-stone/10 px-7 py-6 space-y-4">
                <div className="flex justify-between">
                  <span className="text-stone/60 text-sm uppercase tracking-wider">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-display text-lg">{formatPrice(subtotal)}</span>
                </div>
                <button
                  data-testid="checkout-button"
                  onClick={startCheckout}
                  className="btn-slide w-full bg-stone text-obsidian font-display uppercase tracking-[0.2em] py-4 text-sm"
                >
                  <span className="btn-label">Checkout</span>
                  <span className="btn-label-dup">Beyond your limits</span>
                </button>
                <p className="text-[11px] text-stone/40 text-center leading-relaxed">
                  Secure checkout powered by Ecwid — payment and shipping are completed there.
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
